import * as T from './types';
import TenantObserver, { TenantEvents } from '../tenantHandler/TenantObserver';
import { SetServiceDependencies } from '../../infra/startup/types';
import { RoutesService } from '../RoutesService';
import bindAllMethods from '../../utils/bindAllMethods';
import ITenantHandlerService from '../tenantHandler/ITenantHandlerService';
import BaseBreadcrumbService from './BaseBreadcrumbService';
import IBreadcrumbService from './IBreadcrumbService';
import Criterion from '../../interface/v1/Criterion';
import { ICriterionService } from '../criterionService/ICriterionService';

export default class BreadcrumbService
  extends BaseBreadcrumbService
  implements IBreadcrumbService
{
  private _navigation: any;
  private _criterionService: ICriterionService;
  private _criterion: Criterion<any>;
  private _tenantHandlerService: ITenantHandlerService;
  private _routesService: RoutesService;

  constructor(dependencies: T.BreadcrumbServiceDependenciesType) {
    super();

    // TODO: Remove the data from commons default configuration to constructor starter.
    this._isEnabled = !!dependencies?.enable;
    this._defaultSettingsKey = dependencies?.defaultSettingsKey;
    this._settingsList = dependencies?.settingsList || [];

    bindAllMethods(this);
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    this._tenantHandlerService = services.tenantHandlerService;
    this._routesService = services.routesService;
    this._navigation = services.navigationService;
    this._criterionService = services.criterionService;
  }

  public async init(): Promise<void> {
    this._criterion = this._criterionService.getCriterionLegacy();
    const startInitialBreadcrumbHandler = async () => {
      if (!this._isEnabled) return;
      let lastPromiseId = 0;
      const setBreadcrumb = async () => {
        const thisPromiseId = lastPromiseId + 1;
        lastPromiseId = thisPromiseId;
        const currentPath = this._navigation?.location?.pathname;
        const route =
          currentPath &&
          (await this._routesService.findRouteWithPriority(currentPath));
        const newInitialBreadcrumbs: T.BreadcrumbServiceItemType[] = [];

        if (route) {
          const initialBreadcrumbsFromSettings: T.BreadcrumbItemType[] =
            await (async () => {
              const breadcrumbKey =
                route?.breadcrumbKey || route?.breadcrumbKey === false
                  ? route?.breadcrumbKey
                  : this._defaultSettingsKey;

              const settings =
                breadcrumbKey &&
                this._settingsList.find(
                  (context) => context.key === breadcrumbKey
                );

              const breadcrumbListPromise = settings?.initialBreadcrumbs?.map?.(
                async (breadcrumb, index) => {
                  const isCriterionValid = breadcrumb?.criterionKey
                    ? await this._criterion.checkByCriterionKey(
                        breadcrumb?.criterionKey
                      )
                    : true;

                  if (!isCriterionValid) {
                    return undefined;
                  }
                  let key = breadcrumb?.key;
                  let text = breadcrumb?.text;
                  let url = breadcrumb?.url;
                  let translationKey = breadcrumb?.translationKey;

                  if (breadcrumb?.routeKey) {
                    const thisRoute = this._routesService.findRouteByKey(
                      breadcrumb?.routeKey
                    );
                    const routePath = Array.isArray(thisRoute?.path)
                      ? thisRoute?.path?.[0]
                      : thisRoute?.path;

                    if (!key) key = thisRoute?.key;
                    if (!text) text = thisRoute?.label;
                    if (!translationKey)
                      translationKey = thisRoute?.translationKey;
                    if (!url) url = routePath;
                  } else if (breadcrumb?.useTenantName) {
                    const authContext =
                      this._tenantHandlerService.getCurrentContext();
                    const tenantData =
                      this._tenantHandlerService.getTenantByContext(
                        authContext
                      );
                    const tenantName = tenantData?.data?.name;

                    if (!text) text = tenantName;
                  }

                  if (!key) {
                    key = `${settings?.key}-${index}`;
                  }

                  return {
                    key,
                    text,
                    translationKey,
                    url
                  };
                }
              );

              return Promise.all(breadcrumbListPromise).then((response) =>
                response?.filter?.(
                  (responseBreadcrumb) => !!responseBreadcrumb?.key
                )
              );
            })();

          newInitialBreadcrumbs.push(...initialBreadcrumbsFromSettings);

          if (route?.key && route?.label) {
            const haveRouteOnInitialBreadcrumb = newInitialBreadcrumbs.some(
              (breadcrumb) => breadcrumb?.key === route?.key
            );

            if (!haveRouteOnInitialBreadcrumb) {
              newInitialBreadcrumbs.push({
                key: route?.key,
                translationKey: route?.translationKey,
                text: route?.label,
                url: Array.isArray(route?.path) ? route?.path?.[0] : route?.path
              });
            }
          }
        }

        const isSameInitialBreadcrumbs =
          JSON.stringify(newInitialBreadcrumbs) ===
          JSON.stringify(this._initialBreadcrumbs);

        const isLastAndCurrentPromiseEqual = thisPromiseId === lastPromiseId;

        if (!isSameInitialBreadcrumbs && isLastAndCurrentPromiseEqual) {
          this._initialBreadcrumbs = newInitialBreadcrumbs;
          this._breadcrumbs = [];
          this.triggerListeners();
        }
      };

      this._navigation.listen(setBreadcrumb);
      this._criterion.listen(setBreadcrumb);
      // TenantObserver.subscribe(TenantEvents.SET_TENANT, setBreadcrumb);
      TenantObserver.subscribe(
        TenantEvents.SET_TENANT_HANDLER_KEY,
        setBreadcrumb
      );
      await setBreadcrumb();
    };

    await startInitialBreadcrumbHandler();
    this._markAsReady();
  }

  public isEnabled(): boolean {
    return !!this._isEnabled;
  }

  public listen(callback: T.BreadcrumbListenerCallbackType): () => void {
    this._listeners.set(callback, callback);

    return () => this._listeners.delete(callback);
  }

  public useReactListener(React: any): T.BreadcrumbItemType[] {
    const [value, setValue] = React.useState(this.getBreadcrumbs());

    React.useEffect(() => {
      const removeListener = this.listen((breadcrumbs) =>
        setValue(breadcrumbs)
      );

      return () => removeListener();
    }, []);

    return value;
  }

  public getBreadcrumbs(): T.BreadcrumbItemType[] {
    return [...this._initialBreadcrumbs, ...this._breadcrumbs];
  }

  public add(breadcrumbItem: T.BreadcrumbItemType): void {
    if (breadcrumbItem?.key && breadcrumbItem?.text) {
      const index = this._breadcrumbs.findIndex(
        (breadcrumb) => breadcrumb?.key === breadcrumbItem?.key
      );
      let newBreadcrumb = this._breadcrumbs;
      const haveBreadcrumb = index >= 0;
      const isLastBreadcrumbFromList = this._breadcrumbs?.length === index + 1;

      if (!haveBreadcrumb) {
        newBreadcrumb = [...newBreadcrumb, breadcrumbItem];
      } else if (!isLastBreadcrumbFromList) {
        newBreadcrumb = this._breadcrumbs.splice(0, index + 1);
      }

      if (this._breadcrumbs !== newBreadcrumb) {
        this._breadcrumbs = newBreadcrumb;
        this.triggerListeners();
      }
    } else {
      console.warn(
        'Need to provide key and text to add breadcrumb.',
        breadcrumbItem
      );
    }
  }

  public remove(breadcrumbKey: string): void {
    if (!breadcrumbKey) {
      console.warn(`Need to provide breadcrumb key to remove it`);
      return undefined;
    }

    const index = this._breadcrumbs.findIndex(
      (breadcrumb) => breadcrumb?.key === breadcrumbKey
    );

    if (index >= 0) {
      this._breadcrumbs.length = index;
      this.triggerListeners();
    }
  }
}
